import React, { FC, ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import { LuDatabase } from 'react-icons/lu';
import { ConsoleAPIGraph, CreativeHubTypes } from '@adflow/graphqlgen';
import {
  AdElement,
  AdTemplate,
  AdflowAPI,
  EditorSidebarButtonProps,
  EditorState,
  Datasources,
  RecommendersDataSources,
} from '@adflow/types';
import {
  Box,
  Flex,
  FocusLock,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { EditorStoreContext } from '../../';
import { useStore } from '../../hooks/useStore';
import LeftSidebar from '../LeftSidebar';
import Rightsidebar from '../Rightsidebar';
import Toolbox from '../Toolbox';
import Topbar from '../Topbar';
import MultiDataSourcesForm from './MultiDataSourcesForm';

type Module = {
  name: string;
  EditorSidebarButton: FC<EditorSidebarButtonProps>;
  EditorSidebarView?: FC;
};

interface Props {
  modules: Array<Module>;
  onSave?: (ctx: EditorState) => Promise<void>;
  renderPreview?: (args: RenderPreviewArgs) => ReactElement;
  renderTopbar?: () => ReactElement;
  saveButtonVisible?: boolean;
  isEditMode?: boolean;
}

type RenderPreviewArgs = {
  elements: Array<AdElement>;
  importSourceJSON?: string;
  importSourceType?: AdTemplate['importSourceType'];
  brand: ConsoleAPIGraph.Brand | null;
  brandKit?: CreativeHubTypes.Brand | null;
  fonts: Array<{
    fontFamily: string;
    fontFamilyId?: string;
    weight: number;
    style: string;
    stretch?: string;
    src: string;
  }>;
  displayOptions?: AdflowAPI.AdTemplateDisplayOptions;
  selectedElementId: string | null;
  isIndexPreview: boolean;
  dataSources?: Array<{ id: string; type: string }>;
};

type StageMarketConfig = {
  availableMarkets: Datasources.SourceType[];
  needsOddsFormatting: boolean;
};

// TODO: Add logic to filter market types based on the selected template type;
export type SourceConfig = {
  id: string;
  name: string;
  marketTypes: Datasources.SourceType[];
  templateTypes: Datasources.SourceType[];
  multipleMarkets: boolean;
  acceptEmptyMarkets: boolean;
  needsOddsFormatting?: boolean;
  stageMarkets?: Record<string, StageMarketConfig>;
};

// TODO: Move this logic to the @adflow/types package
const sourceConfigs: SourceConfig[] = [
  {
    id: 'SPORTS',
    name: 'Sports',
    marketTypes: RecommendersDataSources.recommendersMarketDataTypes,
    templateTypes: ['EVENT', 'EVENT_COMPETITOR_FOCUSED', 'NEW_EVENT', 'OUTRIGHT'],
    multipleMarkets: true,
    acceptEmptyMarkets: false,
    needsOddsFormatting: true,
  },
  {
    id: 'CAR_RACING',
    name: 'Car Racing',
    marketTypes: [...Datasources.availableMarketTypesForCompetition('NASCAR')],
    templateTypes: ['NASCAR'],
    multipleMarkets: false,
    acceptEmptyMarkets: false,
    needsOddsFormatting: false,
  },
  {
    id: 'CASINO',
    name: 'Casino',
    marketTypes: [],
    templateTypes: ['CASINO'],
    multipleMarkets: false,
    acceptEmptyMarkets: true,
    needsOddsFormatting: false,
  },
  {
    id: 'SPORTS_MOMENTS',
    name: 'Sports Moments',
    marketTypes: [
      ...Datasources.availableMarketTypesForCompetition('NBA'),
      ...Datasources.availableMarketTypesForCompetition('SOCCER'),
    ],
    templateTypes: ['NBA', 'SOCCER'],
    multipleMarkets: false,
    acceptEmptyMarkets: false,
    needsOddsFormatting: false,
    stageMarkets: {
      SOCCER_PRE: {
        availableMarkets: ['FULL_TIME_RESULT'],
        needsOddsFormatting: true,
      },
    },
  },
];

const Editor: FC<Props> = ({
  modules,
  renderPreview,
  renderTopbar,
  onSave,
  saveButtonVisible = true,
  isEditMode = false,
}) => {
  const ctx = useContext(EditorStoreContext);

  const [searchParams] = useSearchParams();
  const templateTypeParam = searchParams.get('templateType');

  const [activeModule, setActiveModule] = useState<string>(
    templateTypeParam ? templateTypeParam.charAt(0).toUpperCase() + templateTypeParam.slice(1) : '',
  );

  const {
    elements,
    brand,
    brandKit,
    fonts,
    displayOptions,
    selectedElementId,
    importSourceJSON,
    importSourceType,
    dataSources,
  } = useStore((state) => ({
    brand: state.brand,
    brandKit: state.brandKit,
    elements: state.elements,
    fonts: state.customFonts,
    displayOptions: state.displayOptions,
    selectedElementId: state.selectedElementId,
    importSourceJSON: state.importSourceJSON,
    importSourceType: state.importSourceType,
    dataSources: state.dataSources,
  }));

  // Filter source configs based on template type
  // SPORTS_MOMENTS (NBA) is not supported for video (creatomate) templates
  const filteredSourceConfigs = useMemo(() => {
    if (importSourceType === 'creatomate') {
      return sourceConfigs.filter((config) => config.id !== 'SPORTS_MOMENTS');
    }
    return sourceConfigs;
  }, [importSourceType]);

  const isIndexPreview = false;
  // const ActiveEditorSidebarView = useMemo(() => {
  //   return modules.find(module => module.name === activeModule)
  //     ?.EditorSidebarView;
  // }, [activeModule, modules]);

  // Reference to the store is stable between renders. As we need
  // access to the full editor state it's best to use this here to
  // avoid unnecessary re-renders. useStore() is good if you want
  // to subscribe to a _part_ of the store state.
  const handleOnSave = useCallback(async () => {
    if (ctx.store != null && onSave != null) {
      await onSave(ctx.store.getState());
    }
  }, [ctx.store, onSave]);

  // const selectedElement = useEditorStore(s => s.selectedElementId);
  const s = useStore((state) => ({
    setSelectedElement: state.setElementSelected,
  }));

  const handleClick = useCallback(() => {
    s.setSelectedElement(null);
  }, [s]);

  return (
    <Flex flex={1} width="100%" height="100%" direction="column" overflow="auto">
      {renderTopbar ? (
        renderTopbar()
      ) : (
        <Topbar
          onSave={handleOnSave}
          saveButtonVisible={saveButtonVisible}
          sources={filteredSourceConfigs}
        />
      )}
      {/* fill full height - height of toolbar */}
      <Flex flex={1}>
        <Toolbox>
          {modules.map((M) => (
            <M.EditorSidebarButton
              key={M.name}
              openSidebarView={(value) => {
                setActiveModule(value);
              }}
              selected={activeModule === M.name}
              disableAdvertiserSelection={isEditMode}
            ></M.EditorSidebarButton>
          ))}
          <DataSourceBtn />
        </Toolbox>
        <LeftSidebar />
        <Box flexGrow={1} onClick={handleClick} />
        <Box m={'auto'}>
          {renderPreview &&
            renderPreview({
              elements,
              brand,
              brandKit,
              fonts,
              displayOptions,
              selectedElementId,
              isIndexPreview,
              importSourceJSON,
              importSourceType,
              dataSources,
            })}
        </Box>
        <Box flexGrow={1} onClick={handleClick} />
        <Rightsidebar />
      </Flex>
    </Flex>
  );
};

Editor.displayName = 'Editor';

export default Editor;
export type { Props as EditorProps };
export type { RenderPreviewArgs };
const DataSourceBtn: FC<Record<string, unknown>> = () => {
  const { onOpen, onClose, isOpen } = useDisclosure();
  const firstFieldRef = React.useRef(null);
  const importSourceType = useStore((state) => state.importSourceType);

  // Filter source configs based on template type
  // SPORTS_MOMENTS (NBA) is not supported for video (creatomate) templates
  const filteredSources = useMemo(() => {
    if (importSourceType === 'creatomate') {
      return sourceConfigs.filter((config) => config.id !== 'SPORTS_MOMENTS');
    }
    return sourceConfigs;
  }, [importSourceType]);

  return (
    <Popover
      isLazy
      isOpen={isOpen}
      initialFocusRef={firstFieldRef}
      onOpen={onOpen}
      onClose={onClose}
      placement="right"
      closeOnBlur={true}
    >
      <PopoverTrigger>
        <IconButton
          size="lg"
          px={4}
          aria-label="Search database"
          icon={<Icon as={LuDatabase} />}
          borderRadius="100%"
          aspectRatio="1/1"
          data-test-id="data-sources-button"
        />
      </PopoverTrigger>
      <PopoverContent w={500} p={5}>
        <FocusLock restoreFocus persistentFocus={false}>
          <PopoverArrow />
          <PopoverCloseButton />
          <MultiDataSourcesForm
            sources={filteredSources}
            firstFieldRef={firstFieldRef}
            onCancel={onClose}
          ></MultiDataSourcesForm>
        </FocusLock>
      </PopoverContent>
    </Popover>
  );
};
